import { Component, OnInit } from '@angular/core';
import { ContractsService } from './contracts.service';
import { Contracts } from './models/contracts.model';
import { MenuItem, MessageService } from 'primeng/api';
import { TableLazyLoadEvent } from "primeng/table";
import { SessionStorageService } from "../services/session-storage.service";
import { Router, RouterModule } from "@angular/router";
import { ContractStatus } from "./models/contract-status.model";
import { CommonModule } from '@angular/common';

// PrimeNG Modules
import { ToastModule } from 'primeng/toast';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { ToolbarModule } from 'primeng/toolbar';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { InputTextModule } from 'primeng/inputtext';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { ProgressBarModule } from "primeng/progressbar";
import { ChipModule } from "primeng/chip";
import { PageShellComponent } from '../shared/page-layout/page-shell.component';
import { PageHeroComponent } from '../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../shared/page-layout/page-metric-strip.component';
import { PageDataPanelComponent } from '../shared/page-layout/page-data-panel.component';
import { PageMetric, PageMetricTone } from '../shared/page-layout/page-layout.models';
import { ContractLifecycleDashboardSummary } from './models/contract-lifecycle.model';


@Component({
    selector: 'app-contracts',
    templateUrl: './contracts.component.html',
    styleUrls: ['./contracts.component.css'],
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        ToastModule,
        ButtonModule,
        TableModule,
        ToolbarModule,
        BreadcrumbModule,
        InputTextModule,
        CardModule,
        TagModule,
        ProgressBarModule,
        ChipModule,
        PageShellComponent,
        PageHeroComponent,
        PageMetricStripComponent,
        PageDataPanelComponent
    ],
    providers: [
        ContractsService,
        MessageService,
        SessionStorageService
    ]
})
export class ContractsComponent implements OnInit {

    contracts: Contracts[] = [];
    contractsCount = 0;
    lifecycleDashboard?: ContractLifecycleDashboardSummary;

    first = 0;
    rows = 20;
    isLoading = false;

    breadcrumbItems: MenuItem[] = [];
    homeBreadcrumb: MenuItem = {};

    constructor(private contractsService: ContractsService,
        private router: Router,
        private sessionStorageService: SessionStorageService) { }

    ngOnInit(): void {
        this.breadcrumbItems = [{ label: 'Contracts' }];
        this.homeBreadcrumb = { icon: 'pi pi-home', routerLink: '/' };
        this.loadLifecycleDashboard();
    }

    loadContracts(event: TableLazyLoadEvent) {
        this.isLoading = true;
        this.first = event.first ?? 0;
        this.rows = event.rows ?? 20;
        let page = event.first ?? 0;
        page = page / 20;
        let pageSize = event.rows ?? 20;
        if (pageSize == 0)
            pageSize = 20;
        this.contractsService.getAllContracts(page, pageSize, this.sessionStorageService.getCompany()?.id ?? 0).subscribe(data => {
            console.log(data);
            this.contracts = data.items;
            this.contracts.forEach(value => {
                value.contractStatus = value.revisions
                    .sort((a, b) => a.revisionNumber - b.revisionNumber)
                    .map(value => value.status).pop() || ''
            })
            this.contractsCount = data.count;
            this.isLoading = false;
        });
    }

    getContractMetrics(): PageMetric[] {
        const active = this.contracts.filter(contract => contract.contractStatus === ContractStatus.ACTIVE).length;
        const draftPending = this.contracts.filter(contract =>
            contract.contractStatus === ContractStatus.DRAFT || contract.contractStatus === ContractStatus.PENDING
        ).length;
        const expiredTerminated = this.contracts.filter(contract =>
            contract.contractStatus === ContractStatus.EXPIRED || contract.contractStatus === ContractStatus.TERMINATED
        ).length;

        return [
            { label: 'Loaded', value: this.contracts.length, hint: `${this.contractsCount} total records`, tone: 'info' },
            { label: 'Active', value: active, hint: 'Current page', tone: 'success' },
            { label: 'Draft/Pending', value: draftPending, hint: 'Current page', tone: 'warn' },
            { label: 'Expired/Terminated', value: expiredTerminated, hint: 'Current page', tone: 'danger' }
        ];
    }

    loadLifecycleDashboard() {
        this.contractsService.getLifecycleDashboard(this.sessionStorageService.getCompany()?.id).subscribe(dashboard => {
            this.lifecycleDashboard = dashboard;
        });
    }

    getLifecycleDashboardMetrics(): PageMetric[] {
        const counters = this.lifecycleDashboard?.counters ?? {};
        return [
            { label: 'Pending Approval', value: counters['PENDING_APPROVAL'] ?? 0, hint: 'Submitted revisions', tone: this.metricTone(counters['PENDING_APPROVAL']) },
            { label: 'Pending Signature', value: counters['PENDING_SIGNATURE'] ?? 0, hint: 'Approved or signature wait', tone: this.metricTone(counters['PENDING_SIGNATURE']) },
            { label: 'Unsigned Active', value: counters['UNSIGNED'] ?? 0, hint: 'Needs signed document', tone: this.metricTone(counters['UNSIGNED'], 'danger') },
            { label: 'Expiring 30 Days', value: counters['EXPIRING_30'] ?? 0, hint: `${counters['EXPIRING_90'] ?? 0} within 90 days`, tone: this.metricTone(counters['EXPIRING_30'], 'warn') },
            { label: 'Expired', value: counters['EXPIRED'] ?? 0, hint: 'Expired or past end date', tone: this.metricTone(counters['EXPIRED'], 'danger') },
            { label: 'Missing Rates', value: counters['MISSING_RATES'] ?? 0, hint: 'Active without rate cards', tone: this.metricTone(counters['MISSING_RATES'], 'warn') },
            { label: 'Overdue Obligations', value: counters['OVERDUE_OBLIGATIONS'] ?? 0, hint: 'Open due work', tone: this.metricTone(counters['OVERDUE_OBLIGATIONS'], 'danger') },
            { label: 'Recent Events', value: counters['RECENT_EVENTS'] ?? 0, hint: 'Lifecycle audit rows', tone: 'info' }
        ];
    }

    private metricTone(value: number | undefined, alertTone: PageMetricTone = 'warn'): PageMetricTone {
        return (value ?? 0) > 0 ? alertTone : 'success';
    }

    // Add your addContract, viewContract, editContract, deleteContract methods here
    addContract() {
        this.router.navigate(['/contracts/create']);
    }

    viewContract(contract: Contracts) {
        this.router.navigate(['/contracts/view', contract.contractID]);
    }

    editContract(contract: Contracts) {
        this.router.navigate(['/contracts/edit', contract.contractID]);
        /*this.isLoading = true;
        this.contractsService.updateContract(contract.contractID, contract)
          .subscribe(() => {
            // Handle successful update (e.g., show a success message, reload contracts)
            console.log('Contract updated successfully!');
            this.contractsService.getAllContracts(this.first, this.rows).subscribe(data => {
              this.contracts = data.items;
              this.contractsCount = data.count;
              this.isLoading = false;
            }); // You might want to reload the contracts list
          });*/
    }

    deleteContract(contract: Contracts) {
        if (confirm(`Are you sure you want to delete contract ${contract.contractID}?`)) {
            this.isLoading = true;
            this.contractsService.deleteContract(contract.contractID)
                .subscribe(() => {
                    // Handle successful deletion (e.g., show a success message, reload contracts)
                    console.log('Contract deleted successfully!');
                    this.contractsService.getAllContracts(this.first, this.rows).subscribe(data => {
                        this.contracts = data.items;
                        this.contractsCount = data.count;
                        this.isLoading = false;
                    }); // You might want to reload the contracts list
                });
        }
    }

    getContractSeverity(status: string): string {
        switch (status) {
            case ContractStatus.DRAFT:
                return 'warning';
            case ContractStatus.ACTIVE:
                return 'success';
            case ContractStatus.TERMINATED:
                return 'danger';
            default:
                return 'info';
        }
    }
}
